import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { StrainInput } from '@shared';
import { Download, FileSpreadsheet, PenLine } from 'lucide-react';
import { StrainForm } from '../components/StrainForm';
import { CreateProductImages } from '../components/CreateProductImages';
import { getProductImagesApi } from '../lib/productImages';
import { getProductRepository } from '../lib/productRepository';
import { legacyServerToolsEnabled } from '../lib/runtimeFeatures';
import {
  createProductWithStagedImages,
  type StagedProductImage,
} from '../lib/stagedProductImages';

type Mode = 'choose' | 'manual';

/**
 * Add Product: choose import path or add manually.
 * Server mode keeps Spreadsheet → Settings. Local mode offers one-way
 * bootstrap from the existing server catalogue into IndexedDB.
 */
export function AddStrain(): JSX.Element {
  const [mode, setMode] = useState<Mode>('choose');
  const [busy, setBusy] = useState(false);
  const [stagedImages, setStagedImages] = useState<StagedProductImage[]>([]);
  const [primaryImageKey, setPrimaryImageKey] = useState<string | null>(null);
  const [imageSaveIssue, setImageSaveIssue] = useState<{
    productId: string;
    savedCount: number;
    uploadFailureCount: number;
    primaryFailed: boolean;
  } | null>(null);
  const navigate = useNavigate();
  const repositoryMode = getProductRepository().mode;
  const canImportLegacyCatalogue =
    repositoryMode === 'local' && legacyServerToolsEnabled();

  useEffect(() => {
    if (mode !== 'choose' || stagedImages.length === 0) return;
    // CreateProductImages revokes its owned preview URLs when it unmounts.
    setStagedImages([]);
    setPrimaryImageKey(null);
  }, [mode, stagedImages.length]);

  const handleCreate = async (input: StrainInput): Promise<void> => {
    setBusy(true);
    try {
      const result = await createProductWithStagedImages(
        { ...input, sourceType: 'local_manual' },
        stagedImages,
        primaryImageKey,
        getProductRepository(),
        getProductImagesApi(),
      );
      if (result.images.failures.length > 0) {
        setImageSaveIssue({
          productId: result.product.id,
          savedCount: result.images.savedCount,
          uploadFailureCount: result.images.failures.filter(
            (failure) => failure.operation === 'upload',
          ).length,
          primaryFailed: result.images.failures.some(
            (failure) => failure.operation === 'primary',
          ),
        });
        return;
      }
      navigate(`/strains/${result.product.id}`);
    } finally {
      setBusy(false);
    }
  };

  if (imageSaveIssue) {
    return (
      <div className="stack">
        <h1 className="page-title">Product created</h1>
        <div className="notice" role="alert">
          The product was saved
          {imageSaveIssue.savedCount > 0
            ? ` with ${imageSaveIssue.savedCount} image${imageSaveIssue.savedCount === 1 ? '' : 's'}`
            : ''}
          {imageSaveIssue.uploadFailureCount > 0
            ? `, but ${imageSaveIssue.uploadFailureCount} image${
                imageSaveIssue.uploadFailureCount === 1 ? ' could' : 's could'
              } not be saved`
            : ''}
          {imageSaveIssue.primaryFailed
            ? `${imageSaveIssue.uploadFailureCount > 0 ? ' and' : ', but'} the selected Primary image could not be applied`
            : ''}
          . Open the product and use Manage Images to finish the image setup.
        </div>
        <Link
          className="btn btn-primary btn-lg"
          to={`/strains/${imageSaveIssue.productId}`}
        >
          Open product
        </Link>
      </div>
    );
  }

  if (mode === 'choose') {
    return (
      <div className="stack">
        <h1 className="page-title">Add Product</h1>
        <p className="page-lead">
          {repositoryMode === 'local'
            ? canImportLegacyCatalogue
              ? 'Create a local product, or import from the existing server-backed catalogue into this device.'
              : 'Create a product stored privately in this device.'
            : 'Import from your company spreadsheet, or create a local product that works offline.'}
        </p>

        {canImportLegacyCatalogue ? (
          <Link to="/add/import-catalogue" className="card add-choice">
            <span className="add-choice__icon" aria-hidden>
              <Download size={22} strokeWidth={1.75} />
            </span>
            <span>
              <strong>Import Existing Catalogue</strong>
              <span className="muted" style={{ display: 'block' }}>
                One-way copy from the server catalogue into this device&apos;s
                local library. Does not change Sheets or sync state.
              </span>
            </span>
          </Link>
        ) : repositoryMode === 'server' ? (
          <Link to="/settings" className="card add-choice">
            <span className="add-choice__icon" aria-hidden>
              <FileSpreadsheet size={22} strokeWidth={1.75} />
            </span>
            <span>
              <strong>Import from Spreadsheet</strong>
              <span className="muted" style={{ display: 'block' }}>
                Preview and approve a pull in Settings. Exact Product IDs only.
              </span>
            </span>
          </Link>
        ) : null}

        <button
          type="button"
          className="card add-choice"
          onClick={() => setMode('manual')}
        >
          <span className="add-choice__icon" aria-hidden>
            <PenLine size={22} strokeWidth={1.75} />
          </span>
          <span>
            <strong>Add Manually</strong>
            <span className="muted" style={{ display: 'block' }}>
              Local product — no spreadsheet ID, works offline.
            </span>
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="stack">
      <button type="button" className="btn" onClick={() => setMode('choose')}>
        ← Back
      </button>
      <h1 className="page-title">Add Manually</h1>
      <p className="page-lead">
        Creates a local product (source: Local). No external Product ID is
        assigned.
      </p>
      <StrainForm
        submitLabel="Create local product"
        onSubmit={handleCreate}
        busy={busy}
        forceSourceType="local_manual"
        afterLineage={
          <CreateProductImages
            images={stagedImages}
            primaryKey={primaryImageKey}
            onImagesChange={setStagedImages}
            onPrimaryChange={setPrimaryImageKey}
            disabled={busy}
          />
        }
      />
    </div>
  );
}
